'use client'

import { useState } from 'react'
import { useTranslations } from 'next-intl'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import { useMutation } from '@tanstack/react-query'
import { toast } from 'sonner'
import { KeySquareIcon } from 'lucide-react'
import { Link } from '@/i18n/navigation'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form'
import { InputGroup, InputGroupAddon, InputGroupPassword } from '@/components/ui/input-group'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import { getErrorMessage, getLocalizedRouteFromKeys, setValidationErrors } from '@/lib/utils'
import { PASSWORD_REGEX } from '@/constants'
import { resetPassword } from '@/lib/api'
import { ChangePasswordParamsType } from '@/types/auth'
import { useAppLocale, useMetadata } from '@/hooks'

type PropsType = {
  token: string
  className?: string
}

const ChangePasswordForm = ({ token, className = '' }: PropsType) => {
  const t = useTranslations()
  const { backendLocale } = useAppLocale()
  const { routeParams } = useMetadata()
  const [loading, setLoading] = useState(false)
  const [success, setSuccess] = useState(false)

  const loginRoute = getLocalizedRouteFromKeys('/login', backendLocale, routeParams)

  const formSchema = z
    .object({
      password: z
        .string()
        .min(6, t('min_value', { value: 6 }))
        .max(32, t('max_value', { value: 32 }))
        .refine(val => PASSWORD_REGEX.test(val), t('password_pattern'))
        .refine(val => !/\s/.test(val), t('password_pattern')),
      passwordConfirm: z.string().min(1, t('required'))
    })
    .superRefine((data, ctx) => {
      if (data.password !== data.passwordConfirm) {
        ctx.addIssue({
          code: 'custom',
          message: t('passwords_do_not_match'),
          path: ['passwordConfirm']
        })
      }
    })

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: { password: '', passwordConfirm: '' }
  })

  const { mutate } = useMutation({
    mutationFn: async (data: ChangePasswordParamsType) => resetPassword(token, data),
    onSuccess: () => {
      toast.success(t('password_changed_success'))
      setSuccess(true)
    },
    onError: (error: any) => {
      if (error.response?.status === 422) {
        setValidationErrors(error, form.setError)
      } else if (error.response?.status === 404) {
        toast.error(t('token_invalid_or_expired'))
      } else {
        toast.error(getErrorMessage(error, t('password_changed_error')))
      }
    },
    onSettled: () => setLoading(false)
  })

  const onSubmit = (data: z.infer<typeof formSchema>) => {
    setLoading(true)
    mutate(data)
  }

  if (success) {
    return (
      <div className="flex flex-col items-center gap-4 rounded-md border border-green-200 bg-green-50 p-6 text-center">
        <p className="text-sm text-green-800">{t('password_changed_success')}</p>
        <Link href={loginRoute} className="text-brand text-sm font-medium hover:underline">
          {t('back_to_login')}
        </Link>
      </div>
    )
  }

  return (
    <Form {...form}>
      <div className={`flex w-full flex-col space-y-4 select-none ${className}`}>
        <div className="flex flex-col gap-1 border-b pb-4">
          <h2 className="flex items-center gap-2 text-lg font-semibold uppercase">
            <KeySquareIcon className="h-5 w-5" />
            {t('change_password')}
          </h2>
          <p className="text-muted-foreground text-sm">{t('change_password_description')}</p>
        </div>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <FormLabel required htmlFor="change-password">
                  {t('new_password')}
                </FormLabel>
                <FormControl>
                  <InputGroup>
                    <InputGroupPassword
                      id="change-password"
                      placeholder="••••••••"
                      {...field}
                      autoFocus
                      disabled={loading}
                    />
                    <InputGroupAddon>
                      <KeySquareIcon />
                    </InputGroupAddon>
                  </InputGroup>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="passwordConfirm"
            render={({ field }) => (
              <FormItem>
                <FormLabel required htmlFor="change-password-confirm">
                  {t('confirm_new_password')}
                </FormLabel>
                <FormControl>
                  <InputGroup>
                    <InputGroupPassword
                      id="change-password-confirm"
                      placeholder="••••••••"
                      {...field}
                      disabled={loading}
                    />
                    <InputGroupAddon>
                      <KeySquareIcon />
                    </InputGroupAddon>
                  </InputGroup>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <Button type="submit" className="w-full uppercase" loading={loading} disabled={loading}>
            {t('change_password')}
          </Button>
        </form>

        <Separator />

        <div className="flex justify-center text-sm">
          <Link href={loginRoute} className="text-brand font-medium hover:underline">
            {t('back_to_login')}
          </Link>
        </div>
      </div>
    </Form>
  )
}

export default ChangePasswordForm
